import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { formatAmount } from '../utils/Utils';

interface LightningAmountProps {
  amount: number;
  className?: string;
  animationCount?: number;
}

export const LightningAmount: React.FC<LightningAmountProps> = ({ 
  amount, 
  className,
  animationCount = 1 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const previousAmountRef = useRef<number | null>(null);
  const isFirstRenderRef = useRef(true);

  // Format the amount
  const formattedAmount = amount > 0 ? formatAmount(amount / 1000, true) : '0';

  // Handle animations when amount changes
  useEffect(() => {
    const previousAmount = previousAmountRef.current;
    
    // Skip animation on first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      previousAmountRef.current = amount;
      return;
    }

    // Animate when amount changes from 0 to non-zero, or between different non-zero values
    if (amount > 0 && (previousAmount === 0 || previousAmount === null || amount !== previousAmount)) {
      let currentCount = 0;
      
      const runAnimation = () => {
        if (currentCount < animationCount) {
          setIsAnimating(true);
          setAnimationKey(prev => prev + 1);
          currentCount++;
          
          setTimeout(() => {
            setIsAnimating(false);
            if (currentCount < animationCount) {
              setTimeout(runAnimation, 100);
            }
          }, 600);
        }
      };

      runAnimation();
    }

    previousAmountRef.current = amount;
  }, [amount, animationCount]);

  // Don't render if amount is 0 or invalid
  if (!amount || amount <= 0 || !isFinite(amount)) {
    return null;
  }

  return (
    <div className={clsx('str-chat__message-simple-zapped', className)}>
      <span 
        key={animationKey}
        className={clsx('lightning-amount', {
          'lightning-amount--animating': isAnimating
        })}
      >
        ⚡ {formattedAmount}
      </span>
    </div>
  );
};
